// src/app/services/devexpress-i18n.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { loadMessages, locale } from 'devextreme/localization';
import { ResourceManager } from 'devexpress-dashboard'; // <<< ÖNEMLİ EK

@Injectable({ providedIn: 'root' })
export class DevexpressI18nService {
  private loadedTR = false;
  // The assets are placed under src/assets/devexpress so they will be served
  // from /assets/devexpress at runtime. Keep a fallback to the older
  // 'assets/i18n/devexpress' path just in case.
  private base = 'assets/devexpress';
  private fallbackBase = 'assets/i18n/devexpress';

  constructor(private http: HttpClient) {
    try {
      // Expose service globally for runtime access and debugging
      (window as any).ngxDevexpressI18nService = this;
    } catch (_) {}
  }

  /**
   * Force-fetch and apply the dashboard translation file immediately.
   * Useful when a dashboard control has already initialized before APP_INITIALIZER
   * completed or when you need to re-apply translations at runtime.
   */
  async forceApplyDashboardLocale(): Promise<void> {
    try {
      const primary = `${this.base}/dx-dashboard.tr.json`;
      const fallback = `${this.fallbackBase}/dx-dashboard.tr.json`;
      let data: any = null;
      try {
        data = await firstValueFrom(this.http.get(primary));
        console.info('forceApplyDashboardLocale: loaded from', primary);
      } catch (e) {
        console.warn('forceApplyDashboardLocale: primary failed, trying fallback', e);
        data = await firstValueFrom(this.http.get(fallback));
        console.info('forceApplyDashboardLocale: loaded from', fallback);
      }
      if (data) {
        // <<< ASIL FARK BURADA
        try {
          ResourceManager.setLocalizationMessages(data);
        } catch (rmErr) {
          console.warn('ResourceManager.setLocalizationMessages failed (forceApply):', rmErr);
        }
        try {
          loadMessages(data); // DevExtreme tarafı için zarar vermez
        } catch (lmErr) {
          console.warn('loadMessages failed (forceApply):', lmErr);
        }

        locale('tr');
        console.info('forceApplyDashboardLocale: applied dashboard messages and set locale tr');
      }
    } catch (err) {
      console.error('forceApplyDashboardLocale failed:', err);
    }
  }

  /**
   * Fetch (but do not apply) dashboard messages JSON from assets.
   * Returns the parsed JSON or null if not found.
   */
  async fetchDashboardMessages(): Promise<any | null> {
    const primary = `${this.base}/dx-dashboard.tr.json`;
    const fallback = `${this.fallbackBase}/dx-dashboard.tr.json`;
    try {
      const data = await firstValueFrom(this.http.get(primary));
      console.info('fetchDashboardMessages: loaded from', primary);
      return data;
    } catch (e) {
      console.warn('fetchDashboardMessages: primary failed, trying fallback', e);
      try {
        const data2 = await firstValueFrom(this.http.get(fallback));
        console.info('fetchDashboardMessages: loaded from', fallback);
        return data2;
      } catch (err) {
        console.error('fetchDashboardMessages failed:', err);
        return null;
      }
    }
  }

  async initTurkish(): Promise<void> {
    if (this.loadedTR) {
      locale('tr');
      return;
    }

    try {
      // DevExtreme core widget mesajları (grid, datebox vs.)
      try {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const devextremeCore: any = require('devextreme/localization/messages/tr.json');
        if (devextremeCore) {
          loadMessages(devextremeCore);
        }
      } catch (e) {
        console.warn('Could not load devextreme core messages via require():', e);
      }

      // Dashboard runtime overrides
      const dashboardOverrides: any = {
        'DashboardStringId.ActionExportDashboard': 'Panoyu Dışa Aktar',
        'DashboardStringId.ActionExportTo': 'Dışa Aktar',
        'DashboardStringId.ActionExportToExcel': "Excel'e Dışa Aktar",
        'DashboardStringId.ActionExportToImage': 'Görüntü Olarak Dışa Aktar',
        'DashboardStringId.ActionExportToPdf': "PDF'ye Dışa Aktar",
        'DashboardStringId.ActionPrintPreview': 'Yazdırma Önizlemesi',
        'DashboardStringId.ActionMaximizeDashboardItem': 'Öğeyi Büyüt',
        'DashboardStringId.ActionRestoreDashboardItem': 'Öğeyi Geri Yükle',
        'DashboardStringId.ActionClearSelection': 'Seçimi Temizle',
        'DashboardStringId.ActionClearMasterFilter': 'Ana filtreyi temizle',
        'DashboardStringId.ButtonApply': 'Uygula',
        'DashboardStringId.ButtonCancel': 'İptal',
        'DashboardStringId.ButtonOK': 'Tamam',
        'DashboardStringId.ActionDrillUp': 'Üst Seviyeye Dön',
        'DashboardStringId.ActionSelectDateTimePeriod': 'Tarih/Saat Aralığı Seç',
      };

      try {
        // <<< BURADA DA ResourceManager devreye giriyor
        ResourceManager.setLocalizationMessages(dashboardOverrides);
      } catch (rmErr) {
        console.warn('Could not apply dashboard overrides via ResourceManager:', rmErr);
      }

      try {
        loadMessages(dashboardOverrides);
        console.info('Applied immediate dashboard overrides (Turkish)');
      } catch (ovrErr) {
        console.warn('Could not apply immediate dashboard overrides via loadMessages:', ovrErr);
      }

      // JSON dosyaları
      const files = [
        'dx-analytics-core.tr.json',
        'dx-dashboard.tr.json',
        'dx-reporting.tr.json',
        'dx-rich.tr.json',
        'dx-spreadsheet.tr.json',
      ];

      const loadedParts: string[] = [];

      for (const fname of files) {
        let loaded = false;
        const primaryUrl = `${this.base}/${fname}`;
        const fallbackUrl = `${this.fallbackBase}/${fname}`;

        try {
          const res: any = await firstValueFrom(this.http.get(primaryUrl));

          // <<< TÜM JSON’LARI ResourceManager + loadMessages ile uygula
          try {
            ResourceManager.setLocalizationMessages(res);
          } catch (rmErr) {
            console.warn(`ResourceManager failed for ${fname}:`, rmErr);
          }
          try {
            loadMessages(res);
          } catch (lmErr) {
            console.warn(`loadMessages failed for ${fname}:`, lmErr);
          }

          loaded = true;
          loadedParts.push(primaryUrl);
        } catch (pErr) {
          console.warn(`Could not load ${primaryUrl}:`, pErr);
          try {
            const res2: any = await firstValueFrom(this.http.get(fallbackUrl));

            try {
              ResourceManager.setLocalizationMessages(res2);
            } catch (rmErr) {
              console.warn(`ResourceManager failed for fallback ${fname}:`, rmErr);
            }
            try {
              loadMessages(res2);
            } catch (lmErr) {
              console.warn(`loadMessages failed for fallback ${fname}:`, lmErr);
            }

            loaded = true;
            loadedParts.push(fallbackUrl);
            console.info(`Loaded ${fname} from fallback ${fallbackUrl}`);
          } catch (fErr) {
            console.warn(`Could not load ${fallbackUrl}:`, fErr);
          }
        }

        if (!loaded) {
          console.warn(`Skipping ${fname} — no translation file found in primary or fallback.`);
        }
      }

      console.info('DevExpress translation parts loaded:', loadedParts);

      this.loadedTR = true;
      locale('tr');

      try {
        console.info('DevExpress TR messages loaded (dashboard, analytics, reporting, rich, spreadsheet)');
        console.info('DevExpress locale set to:', 'tr');
      } catch {
        // ignore
      }
    } catch (err) {
      console.error('TR paketleri yüklenemedi:', err);
    }
  }
}
